import { A } from '@adornis/base/env-info';
import { Int } from '@adornis/baseql/baseqlTypes';
import { Entity, Field, Query } from '@adornis/baseql/decorators';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity';
import needle from 'needle';
import { NodeBBTag } from './nodebb-tag';

const NODEBB_API_URL = A.isServer ? process.env.NODEBB_API_URL : undefined;
if (A.isServer && !NODEBB_API_URL) throw new Error('Please define NODEBB_API_URL');

@Entity()
export class NodeBBTopicUser extends AdornisEntity {
  static _class = 'NodeBBTopicUser';
  @Field(type => Int)
  uid!: number;
  @Field(type => String)
  username!: string;
  @Field(type => String)
  displayname!: string;
  @Field(type => String)
  userslug!: string;
  @Field(type => Int)
  reputation!: number;
  @Field(type => Int)
  postcount!: number;
  @Field(type => String)
  picture!: string;
  @Field(type => String)
  signature!: string;
  @Field(type => Int)
  banned!: number;
  @Field(type => String)
  status!: string;
}

@Entity()
export class NodeBBTopicTeaserUser extends AdornisEntity {
  static _class = 'NodeBBTopicTeaserUser';
  @Field(type => Int)
  uid!: number;
  @Field(type => String)
  username!: string;
  @Field(type => String)
  userslug!: string;
  @Field(type => String)
  picture!: string;
}

@Entity()
export class NodeBBTopicTeaser extends AdornisEntity {
  static _class = 'NodeBBTopicTeaser';
  @Field(type => Int)
  pid!: number;
  @Field(type => Int)
  tid!: number;
  @Field(type => String)
  content!: string;
  @Field(type => Int)
  uid!: number;
  @Field(type => Date)
  timestamp!: Date;
  @Field(type => String)
  timestampISO!: string;
  @Field(type => NodeBBTopicTeaserUser)
  user!: NodeBBTopicTeaserUser;
  @Field(type => Int)
  index!: number;
}

@Entity()
export class NodeBBTopicThumb extends AdornisEntity {
  static _class = 'NodeBBTopicThumb';
  @Field(type => Int)
  id!: number;
  @Field(type => String)
  name!: string;
  @Field(type => String)
  url!: string;
}

@Entity()
export class NodeBBTopicCategory extends AdornisEntity {
  static _class = 'NodeBBTopicCategory';
  @Field(type => Int)
  cid!: number;
  @Field(type => String)
  name!: string;
  @Field(type => String)
  slug!: string;
  @Field(type => String)
  icon!: string;
  @Field(type => String)
  backgroundImage!: string;
  @Field(type => String)
  imageClass!: string;
  @Field(type => String)
  bgColor!: string;
  @Field(type => String)
  color!: string;
  @Field(type => Int)
  disabled!: number;
}

@Entity()
export class NodeBBTopic extends AdornisEntity {
  static _class = 'NodeBBTopic';

  @Field(type => Int)
  tid!: number;
  @Field(type => Int)
  uid!: number;
  @Field(type => Int)
  cid!: number;
  @Field(type => String)
  title!: string;
  @Field(type => String)
  slug!: string;
  @Field(type => Int)
  mainPid!: number;
  @Field(type => Int)
  postcount!: number;
  @Field(type => Int)
  viewcount!: number;
  @Field(type => Int)
  postercount!: number;
  @Field(type => Int)
  scheduled!: number;
  @Field(type => Int)
  deleted!: number;
  @Field(type => Int)
  deletedUid!: number;
  @Field(type => String)
  titleRaw!: string;
  @Field(type => Int)
  locked!: number;
  @Field(type => Int)
  pinned!: number;
  @Field(type => Date)
  timestamp!: Date;
  @Field(type => String)
  timestampISO!: string;
  @Field(type => Date)
  lastposttime!: Date;
  @Field(type => String)
  lastposttimeISO!: string;
  @Field(type => Date)
  pinExpiry!: Date;
  @Field(type => String)
  pinExpiryISO!: string;
  @Field(type => Int)
  upvotes!: number;
  @Field(type => Int)
  downvotes!: number;
  @Field(type => Int)
  votes!: number;
  @Field(type => Int)
  teaserPid!: number;
  @Field(type => [NodeBBTopicThumb])
  thumbs!: NodeBBTopicThumb[];
  @Field(type => Int)
  numThumbs!: number;
  @Field(type => NodeBBTopicCategory)
  category!: NodeBBTopicCategory;
  @Field(type => NodeBBTopicUser)
  user!: NodeBBTopicUser;
  @Field(type => NodeBBTopicTeaser)
  teaser!: NodeBBTopicTeaser;
  @Field(type => [NodeBBTag])
  tags!: NodeBBTag[];
  @Field(type => Boolean)
  isOwner!: boolean;
  @Field(type => Boolean)
  ignored!: boolean;
  @Field(type => Boolean)
  unread!: boolean;
  @Field(type => Int)
  bookmark!: number;
  @Field(type => Boolean)
  unreplied!: boolean;
  @Field(type => [String])
  icons!: string[];
  @Field(type => String)
  thumb!: string;
  @Field(type => Int)
  index!: number;

  @Query(type => [NodeBBTopic])
  public static getRecentNodeBBTopics() {
    return async (gqlFields: string) => {
      const url = new URL('/api/recent', NODEBB_API_URL!).href;
      const response = await needle('get', url);
      const body = response.body;
      if (!body.topics || !Array.isArray(body.topics)) throw new Error(body);
      return (body.topics as any[]).map(item => new this(item));
    };
  }
}
